// The host's native BackButton (the ‹ arrow in the messenger's own header).
//
// ALL FOUR hosts expose it on the same surface — Telegram, Eitaa
// (https://developer.eitaa.com/docs/Develop/JsSDK/#backbutton), Soroush and Bale
// (https://docs.bale.ai/miniapp, v1.0, `web_app_setup_back_button`) ship
// `WebApp.BackButton { isVisible, show(), hide(), onClick(cb), offClick(cb) }`.
// Every call is still capability-probed — never branch on a platform NAME here.
//
// Bale was long believed not to have one. It does; what was missing was Bale's own
// SDK (index.html, 2026-09-13) — without it `webApp()` handed back the TELEGRAM
// bridge on Bale and every postEvent went nowhere, BackButton included.
//
// ⚠️ WHY A PRIORITY, NOT A PUSH/POP STACK.
// React runs effects CHILD-FIRST on mount, so a plain LIFO stack would end up
// with the SHELL's handler on top of the questionnaire's — exactly backwards.
// Registrations therefore carry an explicit priority and the highest one wins
// (ties broken by registration order, which is the right answer for siblings).
import { useEffect, useRef } from "react";
import { webApp } from "./platform.js";

// Depth of the surface registering a handler. Deeper = wins.
export const BACK_PRIORITY = {
  screen: 0,    // the shell, routing between screens
  inner:  10,   // a screen with its own steps (the questionnaire wizard)
  modal:  20,   // an overlay on top of everything
};

const entries = [];      // { fn, priority, seq }
let seq = 0;
let bound = false;       // the dispatcher is attached ONCE and never re-attached

function current() {
  let best = null;
  for (const e of entries) {
    if (!best || e.priority > best.priority ||
        (e.priority === best.priority && e.seq > best.seq)) best = e;
  }
  return best;
}

function dispatch() {
  const e = current();
  if (e) { try { e.fn(); } catch (_) {} }
}

function sync() {
  let bb = null;
  try { bb = webApp()?.BackButton || null; } catch (_) {}
  if (!bb) return;
  try {
    if (!bound && bb.onClick) { bb.onClick(dispatch); bound = true; }
    if (entries.length) bb.show?.(); else bb.hide?.();
  } catch (_) {}
}

/**
 * Show the host BackButton while this component is mounted and route its press
 * to `handler`. Pass `active: false` to register nothing (e.g. the first step of
 * a wizard that has no "back" of its own — the shell's handler then takes over).
 *
 * `handler` may be a fresh closure every render; it's read through a ref so the
 * registration itself is stable and doesn't churn the host button.
 */
export function useHostBackButton(handler, { active = true, priority = BACK_PRIORITY.screen } = {}) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!active || typeof ref.current !== "function") return;
    const entry = { fn: () => ref.current?.(), priority, seq: seq++ };
    entries.push(entry);
    sync();
    return () => {
      const i = entries.indexOf(entry);
      if (i >= 0) entries.splice(i, 1);
      sync();
    };
  }, [active, priority]);
}

/**
 * Will the host actually DRAW its BackButton? Used to drop an in-page «‹» that
 * would duplicate it. Requires a real host session (initData present — the SDKs
 * are loaded in a plain browser too, where their BackButton draws nothing) and a
 * version that has the button (>= 1.0; Bale/Eitaa/Soroush numbering, Telegram is
 * far past it).
 */
export function hostDrawsBackButton() {
  try {
    const wa = webApp();
    if (!wa?.BackButton || !wa.initData) return false;
    if (typeof wa.isVersionAtLeast === "function") return !!wa.isVersionAtLeast("1.0");
    const v = String(wa.version || "0").split(".").map(n => parseInt(n, 10) || 0);
    return v[0] >= 1;
  } catch (_) { return false; }
}
